import { HTMLAttributes, useEffect, useState } from 'react';
import Components from '@/website/components/NewLandingPages';
import PartnerStatement from '@/website/components/NewLandingPages/components/partnerStatement/index';
import CommonProductIntroductionView from '@/website/components/NewLandingPages/components/productIntroduction/common';
import WidgetEmbed from '@/website/components/WidgetEmbed';
import GetQuoteSuspendedButton from '@/website/components/buttons/getQuoteSuspendedButton';
import QuickCostCalculatorView from '@/website/components/switchWithJove/quickCostCalculator/index';
import { useQuoteReleatedQuote } from '@/website/hooks/useQuoteReleatedQuote';
import { ContentfulPageProps } from '@/website/lib/getContentfulData';
import {
  InsuranceType,
  PROVIDER_SOURCE,
  goodsInTransitOldCompatibilityKey,
  judgeDisplayBusinessPromotionCustomLandingPage,
  judgeDisplayPromotionDefaultLandingPage,
  judgeDisplayWebsiteLandingTestimonialSection,
  judgeIsActiveBrokerAndPartnerPromotion,
  useJoveBreakpoint,
} from '@jove-frontend/uikit';
import { Box } from '@mui/material';
import clsx from 'clsx';

type GoodsInTransitLandingPageViewProps = HTMLAttributes<HTMLDivElement> &
  ContentfulPageProps & {
    slug: string;
  };
const GoodsInTransitLandingPageView = (props: GoodsInTransitLandingPageViewProps) => {
  const { post, promotion, slug, className } = props;
  const { isMobileLayout } = useJoveBreakpoint();
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const { jumpToQuotePageAction } = useQuoteReleatedQuote();
  const [isActiveBusinessPromotion, setIsActiveBusinessPromotion] = useState(false);

  /**
   * effects
   */
  useEffect(() => {
    setIsActiveBusinessPromotion(judgeIsActiveBrokerAndPartnerPromotion(promotion));
  }, [promotion]);

  /**
   * methods
   */
  const openQuotePageAction = () => {
    jumpToQuotePageAction();
  };

  /** Promotion logic */
  const hasActivePromotion = promotion?.status === 'active';
  const insuranceType = new Map([
    ['goods-in-transit-insurance', 'goods-in-transit'],
    ['business-insurance', 'business-insurance'],
    ['construction-insurance', 'hired-in-plant'],
  ]);
  const queryParams = new URLSearchParams();
  if (hasActivePromotion) {
    queryParams.set('utm_source', 'jove-website');
    queryParams.set('utm_medium', 'promotion');
    queryParams.set('utm_campaign', promotion.slug);
    queryParams.set('promotion_id', promotion.id);
    queryParams.set('display_insurance', insuranceType.get(promotion.insurance_type));
    queryParams.set('providerCode', promotion.insurance_providers[0].code);
  }

  const quoteSrc = hasActivePromotion ? `${process.env.NX_QUOTE_ENDPOINT}/?${queryParams.toString()}` : `${process.env.NX_QUOTE_ENDPOINT}`;
  /** End of promotion logic */

  const baseImgUrl = 'https://storage.googleapis.com/jove-public-images/website/landing-pages/motor-truck-cargo';

  return (
    <Box className={clsx('flex w-full flex-col', isMobileLayout ? 'px-2' : '', className)}>
      <PartnerStatement />
      {hasActivePromotion ? (
        judgeDisplayBusinessPromotionCustomLandingPage(promotion, { insuranceType: goodsInTransitOldCompatibilityKey }) ? (
          <CommonProductIntroductionView insuranceType={goodsInTransitOldCompatibilityKey} />
        ) : judgeDisplayPromotionDefaultLandingPage(promotion) ? (
          <Components.Hero
            post={post}
            setIsWidgetOpen={setIsWidgetOpen}
            bgImage={`${baseImgUrl}/hero-bg.png`}
            insuranceType={goodsInTransitOldCompatibilityKey}
          />
        ) : (
          <Components.PromotionHero
            promotion={promotion}
            imgFallback={`${baseImgUrl}/hero-bg.png`}
            // onClick={() => setIsWidgetOpen(true)}
            onClick={openQuotePageAction}
            post={post}
          />
        )
      ) : (
        <Components.Hero post={post} setIsWidgetOpen={setIsWidgetOpen} bgImage={`${baseImgUrl}/hero-bg.png`} />
      )}
      {!hasActivePromotion && <QuickCostCalculatorView providerCode={PROVIDER_SOURCE().ACCELERANT_GIT.value} />}
      <Components.ProblemSolution
        problemImg={`${baseImgUrl}/problem-image.png`}
        solutionImg={`${baseImgUrl}/solution-image.png`}
        post={post}
        sourceType={slug}
      />
      {!isActiveBusinessPromotion && <Components.SeeInAction post={post} setIsWidgetOpen={setIsWidgetOpen} />}
      <Components.Features post={post} featureImg={`${baseImgUrl}/features.png`} />
      <Components.TypesOfDrivers post={post} typeOneImg={`${baseImgUrl}/courier.png`} typeTwoImg={`${baseImgUrl}/driver.png`} />
      <Components.OurProducts post={post} />
      {!isActiveBusinessPromotion && <Components.WhyJove post={post} />}
      {!isActiveBusinessPromotion && <Components.SwitchToJove post={post} insuranceType={InsuranceType.GOODS_IN_TRANSIT_INSURANCE} />}
      {judgeDisplayWebsiteLandingTestimonialSection(promotion) && (
        <Components.Testimonial imgSrc={`${baseImgUrl}/business-owner-image.png`} post={post} />
      )}
      {!isActiveBusinessPromotion && <Components.Referral post={post} promotion={promotion} />}
      {!isActiveBusinessPromotion && <Components.Explore post={post} />}
      {!isActiveBusinessPromotion && <Components.FAQ post={post} />}
      {isActiveBusinessPromotion && <GetQuoteSuspendedButton />}
      <WidgetEmbed isOpen={isWidgetOpen} quoteSrc={quoteSrc} />
    </Box>
  );
};

export default GoodsInTransitLandingPageView;
